import {useMemo} from 'react'
import {getDisabledDates, getUnavailableSlots, filterAvailableTimeSlots} from '@/features/agenda/utils'
import {ObjectKeys} from '@/utilities/helpers'
import dayjs from 'dayjs'
import {EventSmall} from '@/features/agenda/types'
import {createDateTime, formatDate} from '@/dayjs'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'

interface DisabledSlot {
    start: Date
    end: Date
}

interface UseDisabledSlotsProps {
    availabilities?: Record<string, string[]> | null
    patientsEvents?: EventSmall[]
    targetDate?: Date
    targetMonth?: Date
    duration?: string | null
    patients?: Array<{id: string}>
}

interface UseDisabledSlotsReturn {
    consultantUnavailableDates: Date[]
    patientUnavailableDates: Date[]
    consultantDisabledSlots: DisabledSlot[]
    patientDisabledSlots: DisabledSlot[]
    includedSlots: string[]
    disabledDates: Date[]
    allMonthDisabled: boolean
    warningDates: Date[]
}

/**
 * Custom hook for calculating all types of disabled slots and dates
 * Handles consultant availabilities, patient absences, time slot conflicts, and holidays
 */
export const useDisabledSlots = ({
    availabilities,
    patientsEvents,
    targetDate,
    targetMonth,
    duration,
    patients
}: UseDisabledSlotsProps): UseDisabledSlotsReturn => {
    // Get holidays for the target date year or current year
    const holidaysYear = targetDate ? dayjs(targetDate).year() : dayjs().year()
    const {data: holidays = []} = useGetHolidays({
        urlParams: {year: holidaysYear || dayjs().year()},
        options: {
            enabled: !!holidaysYear
        }
    })

    // Extract holiday dates
    const holidayDates = useMemo(() => {
        return holidays.map(holiday => dayjs(holiday.date).toDate())
    }, [holidays])

    // Extract patient absence dates from events
    const patientUnavailableDates = useMemo(() => {
        if (!patientsEvents) return []

        return patientsEvents
            .filter(patientEvent => patientEvent.type === 'patient_absence')
            .map(patientEvent => dayjs(patientEvent.date).toDate())
    }, [patientsEvents])

    // Calculate consultant unavailable dates based on availability and duration
    const consultantUnavailableDates = useMemo(() => {
        if (!availabilities || !duration) return []
        return getDisabledDates(availabilities, duration)
    }, [availabilities, duration])

    // Calculate patient disabled time slots (occupied appointments)
    const patientDisabledSlots = useMemo(() => {
        if (!patientsEvents || !patients?.length) return []

        const activePatientEvents = patientsEvents.filter(
            patientEvent =>
                patientEvent.type === 'event' &&
                patientEvent.status !== 'provisional' &&
                patientEvent.status !== 'cancelled' &&
                patientEvent.status !== 'withdrawn'
        )

        return activePatientEvents.map(event => {
            // Create datetime string with fallback time
            const eventStart = createDateTime(dayjs(event.date).toDate(), '00:00')
            const eventEnd = dayjs(eventStart)
                .add(event.duration ?? 60, 'minutes')
                .toDate()

            return {
                start: eventStart,
                end: eventEnd
            }
        })
    }, [patientsEvents, patients])

    // Calculate consultant disabled time slots for the target date
    const consultantDisabledSlots = useMemo(() => {
        if (!availabilities || ObjectKeys(availabilities).length === 0 || !targetDate) return []
        return getUnavailableSlots(availabilities, targetDate)
    }, [availabilities, targetDate])

    // Calculate available time slots for the target date
    const includedSlots = useMemo(() => {
        if (!availabilities || ObjectKeys(availabilities).length === 0 || !targetDate || !duration) return []

        const targetDateStr = formatDate(targetDate)
        const dateSlotKey = ObjectKeys(availabilities).find(dateKey => formatDate(dateKey) === targetDateStr)

        if (!dateSlotKey) return []

        const availableTimes = availabilities[dateSlotKey]
        return filterAvailableTimeSlots(availableTimes, duration)
    }, [availabilities, targetDate, duration])

    // Calculate weekend dates for the target month
    const weekendDates = useMemo(() => {
        if (!targetDate) return []

        const initialDayOfMonth = dayjs(targetMonth).startOf('month')
        const sundaysInMonth: Date[] = []
        const daysInMonth = dayjs(initialDayOfMonth).daysInMonth()

        for (let i = 0; i < daysInMonth; i++) {
            const day = initialDayOfMonth.add(i, 'day')
            if (day.day() == 0) {
                sundaysInMonth.push(day.toDate())
            }
        }

        return sundaysInMonth
    }, [targetMonth])

    // Combine all disabled dates for date picker including holidays and sundays
    const disabledDates = useMemo(() => {
        return [...holidayDates, ...weekendDates]
    }, [holidayDates, weekendDates, targetMonth])

    // Combine all warning dates for date picker including consultant unavailable dates and patient unavailable dates
    const warningDates = useMemo(() => {
        const allWarningDates = [...consultantUnavailableDates, ...patientUnavailableDates]

        // Filter out dates that are already in disabledDates (holidays and weekends)
        return allWarningDates.filter(
            warningDate =>
                !disabledDates.some(
                    disabledDate => dayjs(disabledDate).format('YYYY-MM-DD') == dayjs(warningDate).format('YYYY-MM-DD')
                )
        )
    }, [consultantUnavailableDates, patientUnavailableDates, disabledDates, targetMonth])

    return {
        consultantUnavailableDates,
        patientUnavailableDates,
        consultantDisabledSlots,
        patientDisabledSlots,
        includedSlots,
        disabledDates,
        warningDates,
        allMonthDisabled:
            (availabilities && ObjectKeys(availabilities).every(date => availabilities[date].length === 0)) ?? false
    }
}
